import React, { useState } from "react";
import {
    BarChart3, Globe, MousePointerClick, Smartphone, Monitor, ExternalLink,
    TrendingUp, Users, Eye, Target,
} from "lucide-react";

const num = (n) => Number(n || 0).toLocaleString();

function KpiCard({ icon: Icon, label, value, accent = "", subline, testid }) {
    return (
        <div className={`ll-card ${accent}`} data-testid={testid}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1.5">
                <Icon className="h-3.5 w-3.5" /> {label}
            </div>
            <div className="font-serif text-3xl font-bold tabular-nums leading-tight">{value}</div>
            {subline && <div className="text-[11px] text-muted-foreground mt-1">{subline}</div>}
        </div>
    );
}

function HBar({ label, value, max, sub, testid }) {
    const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
    return (
        <div className="space-y-1" data-testid={testid}>
            <div className="flex items-baseline justify-between gap-2 text-xs">
                <span className="font-medium truncate">{label}</span>
                <span className="tabular-nums font-semibold">{num(value)}{sub && <span className="text-muted-foreground font-normal ml-1">{sub}</span>}</span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}

function TinyBars({ rows }) {
    const max = Math.max(1, ...rows.map((r) => r.pageviews || 0));
    return (
        <div className="flex items-end gap-1.5 h-32 mt-2">
            {rows.map((r) => {
                const h = Math.max(4, Math.round((r.pageviews / max) * 120));
                const adsH = Math.max(0, Math.round(((r.ads_clicks || 0) / max) * 120));
                return (
                    <div key={r.day} className="flex-1 flex flex-col items-center gap-1" data-testid={`trend-day-${r.day}`}>
                        <div className="text-[9px] text-muted-foreground tabular-nums">{r.pageviews}</div>
                        <div className="relative w-full flex justify-center">
                            <div className="w-full max-w-[28px] bg-primary/30 rounded-t" style={{ height: `${h}px` }}>
                                <div className="w-full bg-primary rounded-t" style={{ height: `${adsH}px` }} title={`${r.ads_clicks} Google Ads clicks`} />
                            </div>
                        </div>
                        <div className="text-[9px] text-muted-foreground">{r.day.slice(5)}</div>
                    </div>
                );
            })}
        </div>
    );
}

/**
 * Pure-render dashboard. Used by both:
 *   - AdminTrafficTab (with a window switcher + Refresh + Share button)
 *   - PublicTrafficReportPage (read-only via /r/:token)
 * The window switcher is internal (default 'last_7d') so the public report has
 * the same flexibility without needing an external state owner.
 */
export default function TrafficReportView({ data, readOnly = false }) {
    const [windowKey, setWindowKey] = useState("last_7d");
    if (!data) return null;
    const w = data.windows[windowKey];
    const totalDevice = (data.device_split.mobile || 0) + (data.device_split.desktop || 0);
    const mobilePct = totalDevice ? Math.round((data.device_split.mobile / totalDevice) * 100) : 0;
    const desktopPct = 100 - mobilePct;
    const maxSrc = Math.max(1, ...(data.top_sources || []).map((s) => s.pageviews));
    const maxPath = Math.max(1, ...(data.top_paths || []).map((p) => p.pageviews));

    return (
        <div className="space-y-6" data-testid="traffic-report-view">
            {/* Window selector */}
            <div className="flex items-center gap-2 flex-wrap print:hidden">
                <span className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mr-1">Window</span>
                {[
                    ["today", "Today"],
                    ["last_7d", "Last 7 days"],
                    ["last_30d", "Last 30 days"],
                    ["last_90d", "Last 90 days"],
                    ["all_time", "All time"],
                ].map(([k, l]) => (
                    <button key={k} type="button" onClick={() => setWindowKey(k)}
                            data-testid={`traffic-window-${k}`}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${windowKey === k ? "bg-foreground text-background border-foreground" : "border-border text-foreground/70 hover:bg-accent"}`}>
                        {l}
                    </button>
                ))}
            </div>

            {/* KPI grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="traffic-kpis">
                <KpiCard icon={Eye} label="Page views" value={num(w.pageviews)} subline={windowKey.replace("_", " ")} testid="kpi-pageviews" />
                <KpiCard icon={Users} label="Unique visitors" value={num(w.unique_visitors)} subline="deduped by browser" testid="kpi-visitors" />
                <KpiCard icon={Target} label="Google Ads clicks" value={num(w.google_ads_clicks)} subline="matched via gclid"
                         accent="border-primary/40 bg-primary/5" testid="kpi-google-ads" />
                <KpiCard icon={TrendingUp} label="All-time page views" value={num(data.windows.all_time.pageviews)} subline={`${num(data.windows.all_time.unique_visitors)} unique visitors`} testid="kpi-alltime" />
            </div>

            {/* Funnel */}
            <section className="ll-card" data-testid="traffic-funnel">
                <h3 className="font-serif text-lg font-semibold mb-3 flex items-center gap-2"><BarChart3 className="h-4 w-4 text-primary" /> Conversion funnel — last 30 days</h3>
                <div className="grid grid-cols-3 gap-3">
                    {[
                        { label: "Visitors", value: data.funnel_30d.visitors, color: "bg-primary" },
                        { label: "Questions asked", value: data.funnel_30d.questions_asked, color: "bg-amber-500" },
                        { label: "Find-attorney clicks", value: data.funnel_30d.find_attorney_clicks, color: "bg-green-500" },
                    ].map((s, i) => {
                        const baseline = data.funnel_30d.visitors || 1;
                        const ratio = s.value / baseline;
                        const widthPct = Math.max(8, Math.min(100, Math.round(ratio * 100)));
                        return (
                            <div key={s.label} className="space-y-1.5" data-testid={`funnel-step-${i}`}>
                                <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{s.label}</div>
                                <div className="font-serif text-2xl font-bold tabular-nums">{num(s.value)}</div>
                                <div className="h-2 rounded-full bg-muted overflow-hidden">
                                    <div className={`h-full ${s.color} rounded-full`} style={{ width: `${widthPct}%` }} />
                                </div>
                                {i > 0 && baseline > 0 && (
                                    <div className="text-[11px] text-muted-foreground">
                                        {s.value > baseline
                                            ? `${num(s.value - baseline)} more than visitors (legacy data)`
                                            : `${Math.round((s.value / baseline) * 100)}% of visitors`}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* Daily trend */}
            <section className="ll-card" data-testid="traffic-trend">
                <div className="flex items-baseline justify-between gap-3 mb-1">
                    <h3 className="font-serif text-lg font-semibold flex items-center gap-2"><TrendingUp className="h-4 w-4 text-primary" /> Daily trend — last 7 days</h3>
                    <div className="text-[11px] text-muted-foreground">Filled bar = Google Ads clicks · light bar = total page views</div>
                </div>
                {data.daily_trend.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-6 text-center">No traffic yet in the last 7 days.</div>
                ) : (
                    <TinyBars rows={data.daily_trend} />
                )}
            </section>

            {/* Sources + landing pages */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <section className="ll-card" data-testid="traffic-sources">
                    <h3 className="font-serif text-lg font-semibold mb-3 flex items-center gap-2"><Globe className="h-4 w-4 text-primary" /> Top traffic sources — last 30 days</h3>
                    {(data.top_sources || []).length === 0 ? (
                        <div className="text-sm text-muted-foreground py-4">No data yet.</div>
                    ) : (
                        <div className="space-y-3">
                            {data.top_sources.map((s) => (
                                <HBar key={s.source} label={s.source} value={s.pageviews} max={maxSrc} sub={`· ${num(s.visitors)} visitors`} testid={`source-${s.source}`} />
                            ))}
                        </div>
                    )}
                </section>

                <section className="ll-card" data-testid="traffic-landing-pages">
                    <h3 className="font-serif text-lg font-semibold mb-3 flex items-center gap-2"><MousePointerClick className="h-4 w-4 text-primary" /> Top pages — last 30 days</h3>
                    {(data.top_paths || []).length === 0 ? (
                        <div className="text-sm text-muted-foreground py-4">No data yet.</div>
                    ) : (
                        <div className="space-y-3">
                            {data.top_paths.map((p) => (
                                <HBar key={p.path} label={p.path} value={p.pageviews} max={maxPath} sub={`· ${num(p.visitors)} visitors`} testid={`path-${p.path}`} />
                            ))}
                        </div>
                    )}
                </section>
            </div>

            {/* Campaigns + devices */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <section className="ll-card" data-testid="traffic-campaigns">
                    <h3 className="font-serif text-lg font-semibold mb-3 flex items-center gap-2"><Target className="h-4 w-4 text-primary" /> Campaigns — last 30 days</h3>
                    {(data.campaigns || []).length === 0 ? (
                        <div className="text-sm text-muted-foreground py-4">{readOnly ? "No campaign-tagged traffic yet." : <>No campaign-tagged traffic yet. Add <code className="text-[11px] bg-muted px-1.5 py-0.5 rounded">?utm_campaign=…</code> to your ad URLs.</>}</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                                    <th className="py-1.5 pr-2">Campaign</th>
                                    <th className="py-1.5 pr-2 text-right">Page views</th>
                                    <th className="py-1.5 pr-2 text-right">Visitors</th>
                                    <th className="py-1.5 text-right">Ads clicks</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.campaigns.map((c) => (
                                    <tr key={c.campaign} className="border-t border-border" data-testid={`campaign-${c.campaign}`}>
                                        <td className="py-2 pr-2 font-medium">{c.campaign}</td>
                                        <td className="py-2 pr-2 text-right tabular-nums">{num(c.pageviews)}</td>
                                        <td className="py-2 pr-2 text-right tabular-nums">{num(c.visitors)}</td>
                                        <td className="py-2 text-right tabular-nums font-semibold">{num(c.ads_clicks)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </section>

                <section className="ll-card" data-testid="traffic-devices">
                    <h3 className="font-serif text-lg font-semibold mb-3">Devices — last 30 days</h3>
                    {totalDevice === 0 ? (
                        <div className="text-sm text-muted-foreground py-4">No data yet.</div>
                    ) : (
                        <div className="space-y-3">
                            <div className="flex items-center gap-3">
                                <Smartphone className="h-4 w-4 text-primary flex-shrink-0" />
                                <div className="flex-1">
                                    <div className="flex items-baseline justify-between text-xs mb-1">
                                        <span className="font-medium">Mobile</span>
                                        <span className="tabular-nums">{num(data.device_split.mobile)} <span className="text-muted-foreground">({mobilePct}%)</span></span>
                                    </div>
                                    <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${mobilePct}%` }} /></div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <Monitor className="h-4 w-4 text-primary flex-shrink-0" />
                                <div className="flex-1">
                                    <div className="flex items-baseline justify-between text-xs mb-1">
                                        <span className="font-medium">Desktop</span>
                                        <span className="tabular-nums">{num(data.device_split.desktop)} <span className="text-muted-foreground">({desktopPct}%)</span></span>
                                    </div>
                                    <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary rounded-full" style={{ width: `${desktopPct}%` }} /></div>
                                </div>
                            </div>
                        </div>
                    )}
                </section>
            </div>

            <p className="text-[11px] text-muted-foreground" data-testid="traffic-footnote">
                <ExternalLink className="h-3 w-3 inline mr-1" /> Google Ads clicks are matched to the <code className="bg-muted px-1 py-0.5 rounded">gclid</code> parameter Google auto-appends to every ad URL. This count should line up with the "Clicks" number in your Google Ads dashboard.
            </p>
        </div>
    );
}
